import { useState, useEffect } from "react";
import client from "../api/client";
import { logEmployeeActivity } from "../utils/activityLogger";
import "./Movement.css";

export default function Movement() {
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [form, setForm] = useState({
    item_id: "",
    from_room_id: "",
    to_room_id: "",
    quantity: "",
    employee_name: "",   // optional
    reason: "",          // required
  });

  // Movement History State
  const [movements, setMovements] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("last7days");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 🧩 Load all inventory items, rooms, and employees
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [inventoryRes, roomsRes, employeesRes] = await Promise.all([
          client.get("/inventory"),
          client.get("/rooms"),
          client.get("/movement/employees"),
        ]);
        setItems(inventoryRes.data || []);
        setRooms(roomsRes.data || []);
        
        // Debug employees response
        console.log("🔍 EMPLOYEES API RESPONSE:", employeesRes.data);
        console.log("🔍 EMPLOYEES RESPONSE TYPE:", typeof employeesRes.data);
        console.log("🔍 EMPLOYEES IS ARRAY:", Array.isArray(employeesRes.data));
        
        // Ensure array format
        if (Array.isArray(employeesRes.data)) {
          setEmployees(employeesRes.data);
        } else if (employeesRes.data && employeesRes.data.data) {
          setEmployees(employeesRes.data.data);
        } else if (employeesRes.data && employeesRes.data.employees) {
          setEmployees(employeesRes.data.employees);
        } else {
          console.error("❌ Unexpected employees response format");
          setEmployees([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Mock movement data for testing
  const mockMovements = [
    {
      id: 1,
      item_name: "Laptop Dell",
      sku: "LAP-001",
      from_room: "Warehouse A",
      to_room: "Office Room 1",
      quantity: 5,
      moved_by: "Admin",
      reason: "New employee setup",
      status: "completed",
      created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 2,
      item_name: "Office Chair",
      sku: "CHR-002",
      from_room: "Storage Room",
      to_room: "Office Room 2",
      quantity: 10,
      moved_by: "John Doe",
      reason: "Office expansion",
      status: "completed",
      created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 3,
      item_name: "Monitor LG 24",
      sku: "MON-003",
      from_room: "Warehouse B",
      to_room: "Office Room 3",
      quantity: 3,
      moved_by: "Jane Smith",
      reason: "Replacement request",
      status: "pending",
      created_at: new Date().toISOString()
    },
    {
      id: 4,
      item_name: "Keyboard Mechanical",
      sku: "KEY-004",
      from_room: "Warehouse A",
      to_room: "Office Room 1",
      quantity: 8,
      moved_by: "Mike Johnson",
      reason: "Equipment upgrade",
      status: "completed",
      created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 5,
      item_name: "Mouse Wireless",
      sku: "MOU-005",
      from_room: "Storage Room",
      to_room: "Office Room 2",
      quantity: 15,
      moved_by: "Sarah Wilson",
      reason: "New hire equipment",
      status: "completed",
      created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString()
    }
  ];

  // Set mock movements if no real data
  useEffect(() => {
    if (movements.length === 0) {
      setMovements(mockMovements);
    }
  }, [movements.length]);

  // 🧩 Filter items when from_room_id changes
  useEffect(() => {
    if (form.from_room_id) {
      const filtered = items.filter(item => item.room_id === parseInt(form.from_room_id));
      setFilteredItems(filtered);
    } else {
      setFilteredItems([]);
    }
  }, [form.from_room_id, items]);

  // 🧩 Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Reset item_id when from_room_id changes
    if (name === "from_room_id") {
      setForm({
        ...form,
        [name]: value,
        item_id: "", // Reset selected item
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // 🧩 Submit handler
  const handleMove = async (e) => {
    e.preventDefault();

    if (!form.item_id || !form.from_room_id || !form.to_room_id || !form.quantity) {
      alert("Please fill all fields!");
      return;
    }

    if (!form.reason) {
      alert("Please enter movement reason!");
      return;
    }

    if (form.from_room_id === form.to_room_id) {
      alert("Source and destination room cannot be the same!");
      return;
    }

    try {
      const requestData = {
        item_id: parseInt(form.item_id),
        from_room_id: parseInt(form.from_room_id),
        to_room_id: parseInt(form.to_room_id),
        quantity: parseInt(form.quantity),
        employee_name: form.employee_name, // optional
        reason: form.reason,
      };
      
      console.log("📤 Sending movement request:", requestData);
      
      const res = await client.post("/movement", requestData);
      
      console.log("✅ Movement response:", res.data);
      alert(res.data.message || "✅ Stock moved successfully!");

      // Add new movement to history table immediately
      const newItem = items.find(item => item.id === parseInt(form.item_id));
      const fromRoom = rooms.find(room => room.id === parseInt(form.from_room_id));
      const toRoom = rooms.find(room => room.id === parseInt(form.to_room_id));
      
      const newMovement = {
        id: Date.now(), // temporary ID
        item_name: newItem?.name || "Unknown Item",
        sku: newItem?.sku || "N/A",
        from_room: fromRoom?.room_name || "Unknown Room",
        to_room: toRoom?.room_name || "Unknown Room",
        quantity: parseInt(form.quantity),
        moved_by: form.employee_name || "Current User",
        reason: form.reason,
        status: "completed",
        created_at: new Date().toISOString()
      };
      
      // Add to beginning of movements array
      setMovements(prev => [newMovement, ...prev]);

      setForm({
        item_id: "",
        from_room_id: "",
        to_room_id: "",
        quantity: "",
        employee_name: "",
        reason: "",
      });
    } catch (err) {
      console.error("❌ Error moving item:", err);
      console.error("❌ Error response:", err.response?.data);
      console.error("❌ Request data that failed:", {
        item_id: parseInt(form.item_id),
        from_room_id: parseInt(form.from_room_id),
        to_room_id: parseInt(form.to_room_id),
        quantity: parseInt(form.quantity),
      });
      alert(err.response?.data?.message || "❌ Error occurred while moving stock");
    }
  };

  // Movement History Filtering
  const filteredMovements = movements.filter(movement => {
    const matchesSearch = searchTerm === "" || 
      movement.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.from_room?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.to_room?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Date filtering logic
    const movementDate = new Date(movement.created_at);
    const today = new Date();
    let matchesDate = true;
    
    if (dateFilter === "last7days") {
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      matchesDate = movementDate >= sevenDaysAgo;
    } else if (dateFilter === "last30days") {
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      matchesDate = movementDate >= thirtyDaysAgo;
    }
    
    return matchesSearch && matchesDate;
  });

  // Pagination
  const totalPages = Math.ceil(filteredMovements.length / itemsPerPage);
  const paginatedMovements = filteredMovements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleExport = () => {
    alert("Export functionality will be implemented");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="page-container">
      <h2>📦 Stock Movement</h2>

      <form className="movement-form" onSubmit={handleMove}>
        <select name="from_room_id" value={form.from_room_id} onChange={handleChange}>
          <option value="">From Room</option>
          {rooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.room_name}
            </option>
          ))}
        </select>

        <select name="item_id" value={form.item_id} onChange={handleChange} disabled={!form.from_room_id}>
          <option value="">Select Item</option>
          {filteredItems.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name} (Stock: {item.stock})
            </option>
          ))}
        </select>

        <select name="to_room_id" value={form.to_room_id} onChange={handleChange}>
          <option value="">To Room</option>
          {rooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.room_name}
            </option>
          ))}
        </select>

        <select name="employee_name" value={form.employee_name} onChange={handleChange}>
          <option value="">Select Employee (Optional)</option>
          {employees.map((employee) => (
            <option key={employee.id} value={employee.name}>
              {employee.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          name="reason"
          placeholder="Reason for movement"
          value={form.reason}
          onChange={handleChange}
        />

        <input
          type="number"
          name="quantity"
          placeholder="Quantity"
          min="1"
          value={form.quantity}
          onChange={handleChange}
        />

        <button type="submit">Move</button>
      </form>

      {/* Movement History Section */}
      <div className="movement-history-card">
        <h3 className="movement-history-title">Recent Movement History</h3>
        
        {/* Controls */}
        <div className="movement-controls">
          <input
            type="text"
            placeholder="Search item or room"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="movement-search"
          />
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="movement-date-filter"
          >
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
          <button onClick={handleExport} className="movement-export-btn">
            Export
          </button>
        </div>

        {/* Table */}
        <div className="movement-table-wrapper">
          <table className="movement-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Item Name</th>
                <th>From Room</th>
                <th>To Room</th>
                <th>Quantity</th>
                <th>Moved By</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMovements.length > 0 ? (
                paginatedMovements.map((movement) => (
                  <tr key={movement.id}>
                    <td>{formatDate(movement.created_at)}</td>
                    <td>{formatTime(movement.created_at)}</td>
                    <td>
                      <div className="movement-item">
                        <div className="movement-item-name">{movement.item_name}</div>
                        <div className="movement-item-sku">SKU: {movement.sku || 'N/A'}</div>
                      </div>
                    </td>
                    <td>{movement.from_room}</td>
                    <td>{movement.to_room}</td>
                    <td>{movement.quantity}</td>
                    <td>{movement.moved_by || 'System'}</td>
                    <td>{movement.reason || '-'}</td>
                    <td>
                      <span className={`movement-status ${movement.status?.toLowerCase() || 'completed'}`}>
                        {movement.status === 'completed' ? 'Completed' : 
                         movement.status === 'pending' ? 'Pending' : 
                         movement.status === 'cancelled' ? 'Cancelled' : 'Completed'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="movement-empty">
                    No movement history found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="movement-footer">
          <div className="movement-info">
            Showing {paginatedMovements.length > 0 ? 
              `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, filteredMovements.length)} of ${filteredMovements.length} movements` : 
              '0 movements'
            }
          </div>
          <div className="movement-pagination">
            {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`movement-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
              >
                {i + 1}
              </button>
            ))}
            {totalPages > 3 && (
              <button
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                className="movement-page-btn"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
